// app/javascript/components/ArmyType.jsx
import React, { useState, useMemo, useEffect } from 'react';
import { t } from '../utils/i18n';
const ArmyType = ({ onBack, api }) => {
  const [armyTypes, setArmyTypes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name-asc');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
  });

  useEffect(() => {
    fetchArmyTypes();
  }, []);

  const fetchArmyTypes = async () => {
    try {
      const data = await api.get('/army_types');
      setArmyTypes(data);
    } catch (error) {
      console.error('Failed to fetch army types:', error);
    }
  };

  const filteredAndSortedArmyTypes = useMemo(() => {
    let filtered = armyTypes.filter(armyType =>
      armyType.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      armyType.code?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const [field, order] = sortBy.split('-');
    return filtered.sort((a, b) => {
      let aValue = a[field];
      let bValue = b[field];

      if (field === 'id') {
        aValue = parseInt(aValue);
        bValue = parseInt(bValue);
      }

      if (order === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  }, [armyTypes, searchTerm, sortBy]);

  const handleSortSelect = (sortOption) => {
    setSortBy(sortOption);
    setDropdownOpen(false);
  };

  const handleAdd = () => {
    setFormData({ name: '', code: '' });
    setEditingId(null);
    setShowForm(true);
  };

  const handleEdit = (armyType) => {
    setFormData({
      name: armyType.name || '',
      code: armyType.code || ''
    });
    setEditingId(armyType.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (confirm('Are you sure you want to delete this army type?')) {
      setLoading(true);
      try {
        await api.delete(`/army_types/${id}`);
        setArmyTypes(armyTypes.filter(type => type.id !== id));
      } catch (error) {
        console.error('Failed to delete army type:', error);
        alert('Failed to delete army type');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingId) {
        // Edit existing
        const { response, data } = await api.put(`/army_types/${editingId}`, { army_type: formData });
        if (response.ok) {
          setArmyTypes(armyTypes.map(type =>
            type.id === editingId ? data : type
          ));
        } else {
          alert('Failed to update army type');
        }
      } else {
        // Add new
        const { response, data } = await api.post('/army_types', { army_type: formData });
        if (response.ok) {
          setArmyTypes([...armyTypes, data]);
        } else {
          alert('Failed to create army type');
        }
      }
      setShowForm(false);
      setFormData({ name: '', code: '' });
      fetchArmyTypes();
    } catch (error) {
      console.error('Failed to save army type:', error);
      alert('Failed to save army type');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setFormData({ name: '', code: '' });
    setEditingId(null);
  };

  return (
    <>
      {/* Add/Edit Form */}
      {showForm && (
        <div className="mb_40 baseTableWrap">
          <div className="dFlex baseTableHd mb_16">
            <h2 className="txt20 fwMedium colorBlack">
              {editingId ? 'Edit Army Type' : 'Add New Army Type'}
            </h2>
          </div>
          <form onSubmit={handleSubmit} style={{ padding: '0px 20px' }}>
            <div className="row">
              <div className="col-md-6 mb_20">
                <label className="mb_8">Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                />
              </div>
              <div className="col-md-6 mb_20">
                <label className="mb_8">Code</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.code}
                  onChange={(e) => setFormData({...formData, code: e.target.value})}
                  required
                />
              </div>
            </div>
            <div className="dFlex mt_18 btnSet">
              <button type="submit" className="btnBig me-3" disabled={loading}>
                {loading ? 'Saving...' : (editingId ? t('update') : t('add'))}
              </button>
              <button type="button" className="btnBig btnSecondary" onClick={handleCancel}>
                {t('cancel')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Army Type List */}
      <div className="mb_40 baseTableWrap">
        <div className="dFlex baseTableHd mb_16">
          <h2 className="txt20 fwMedium colorBlack">Army Type List ({filteredAndSortedArmyTypes.length})</h2>
          <div className="dFlex baseTableTR">
            <button className="btnAdd me-3" onClick={handleAdd}>
              Add Army Type
            </button>
            <div className="searchBox">
              <input
                type="text"
                placeholder="Search army types..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <button type="button">
                <img src="/images/search1.png" alt="" />
              </button>
            </div>
            <div className="dropdown dropdownSortBy">
              <button
                className="dropdown-toggle"
                type="button"
                onClick={() => setDropdownOpen(!dropdownOpen)}
              >
                {t('sort_by')}
              </button>
              <ul className={`dropdown-menu ${dropdownOpen ? 'show' : ''}`}>
                <li><a className="dropdown-item" href="#" onClick={() => handleSortSelect('name-asc')}>Name A-Z</a></li>
                <li><a className="dropdown-item" href="#" onClick={() => handleSortSelect('name-desc')}>Name Z-A</a></li>
                <li><a className="dropdown-item" href="#" onClick={() => handleSortSelect('code-asc')}>Code A-Z</a></li>
                <li><a className="dropdown-item" href="#" onClick={() => handleSortSelect('created-desc')}>Newest First</a></li>
              </ul>
            </div>
          </div>
        </div>
        <div className="baseTableContainer">
          <table className="baseTable">
            <thead>
              <tr>
                <th>ID</th>
                <th>{t('name')}</th>
                <th>{t('code')}</th>
                <th>{t('created_date')}</th>
                <th style={{ minWidth: "175px" }}>{t('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedArmyTypes.length > 0 ? (
                filteredAndSortedArmyTypes.map((armyType) => (
                  <tr key={armyType.id}>
                    <td>{armyType.id}</td>
                    <td>{armyType.name}</td>
                    <td>{armyType.code}</td>
                    <td>{armyType.created_at ? new Date(armyType.created_at).toLocaleDateString() : ''}</td>
                    <td>
                      <div className="dFlex optBlock">
                        <button className="optBtn" onClick={() => handleEdit(armyType)} disabled={loading}>
                          <img src="/images/edit.png" alt="" />
                          <span>{t('edit')}</span>
                        </button>
                        <span className="bar">|</span>
                        <button className="optBtn" onClick={() => handleDelete(armyType.id)} disabled={loading}>
                          <img src="/images/delete.png" alt="" />
                          <span>{t('delete')}</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" style={{textAlign: 'center', padding: '20px'}}>
                    No army types found matching "{searchTerm}"
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default ArmyType;
